import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../securityTab.module.scss';
import { validatePasswordField } from 'utils/validation';
import { useProfile } from 'hooks/useProfile';
import { Alert } from 'components/alert';
import { ActionButton } from 'components/actionButton';

const ChangePassword: React.FC = () => {
  const { t } = useTranslation();
  const { changePassword, apiError, successMessage, setApiError, setSuccessMessage } = useProfile();
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChanging, setIsChanging] = useState(false);

  const [errors, setErrors] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleSave = async () => {
    let isValid = true;
    setApiError('');
    setSuccessMessage('');
    const newErrors = { oldPassword: '', newPassword: '', confirmPassword: '' };

    const oldPassError = validatePasswordField(oldPassword);
    if (oldPassError) {
      newErrors.oldPassword = oldPassError;
      isValid = false;
    }

    const newPassError = validatePasswordField(newPassword);
    if (newPassError) {
      newErrors.newPassword = newPassError;
      isValid = false;
    }

    const confirmPassError = validatePasswordField(confirmPassword);
    if (confirmPassError) {
      newErrors.confirmPassword = confirmPassError;
      isValid = false;
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = t('settings.security.password.mismatch');
      isValid = false;
    }

    setErrors(newErrors);

    if (isValid) {
      setIsChanging(true);
      try {
        const success = await changePassword(
          {
            currentPassword: oldPassword,
            newPassword: newPassword,
          },
          false,
        );

        if (success) {
          setOldPassword('');
          setNewPassword('');
          setConfirmPassword('');
          setErrors({ oldPassword: '', newPassword: '', confirmPassword: '' });
          setShowOldPassword(false);
          setShowNewPassword(false);
          setShowConfirmPassword(false);
        }
      } finally {
        setIsChanging(false);
      }
    }
  };

  return (
    <div className={styles.card}>
      <h3 className={styles.cardTitle}>{t('settings.security.password.title')}</h3>
      <p className={styles.cardDescription}>{t('settings.security.password.desc')}</p>
      <div className={styles.passwordGrid}>
        <div className={styles.inputGroup}>
          <label>{t('settings.security.password.currentPassword')}</label>
          <div className={styles.inputWrapper}>
            <input
              type={showOldPassword ? 'text' : 'password'}
              className={`${styles.inputField} ${errors.oldPassword ? styles.inputError : ''}`}
              placeholder={t('settings.security.password.currentPasswordPlaceholder')}
              value={oldPassword}
              onChange={(e) => {
                setOldPassword(e.target.value);
                if (errors.oldPassword) setErrors({ ...errors, oldPassword: '' });
              }}
            />
            <button className={styles.eyeBtn} onClick={() => setShowOldPassword(!showOldPassword)}>
              {showOldPassword ? (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              )}
            </button>
          </div>
          {errors.oldPassword && <span className={styles.errorMsg}>{errors.oldPassword}</span>}
        </div>
        <div className={styles.inputGroup}>
          <label>{t('settings.security.password.newPassword')}</label>
          <div className={styles.inputWrapper}>
            <input
              type={showNewPassword ? 'text' : 'password'}
              className={`${styles.inputField} ${errors.newPassword ? styles.inputError : ''}`}
              placeholder={t('settings.security.password.newPasswordPlaceholder')}
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                if (errors.newPassword) setErrors({ ...errors, newPassword: '' });
              }}
            />
            <button className={styles.eyeBtn} onClick={() => setShowNewPassword(!showNewPassword)}>
              {showNewPassword ? (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              )}
            </button>
          </div>
          {errors.newPassword && <span className={styles.errorMsg}>{errors.newPassword}</span>}
        </div>

        <div className={styles.inputGroup}>
          <label>{t('settings.security.password.confirmPassword')}</label>
          <div className={styles.inputWrapper}>
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              className={`${styles.inputField} ${errors.confirmPassword ? styles.inputError : ''}`}
              placeholder={t('settings.security.password.confirmPasswordPlaceholder')}
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: '' });
              }}
            />
            <button
              className={styles.eyeBtn}
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              )}
            </button>
          </div>
          {errors.confirmPassword && (
            <span className={styles.errorMsg}>{errors.confirmPassword}</span>
          )}
        </div>
        <div className={styles.inputGroup_btn}>
          <ActionButton className={styles.saveBtn} onClick={handleSave} isLoading={isChanging}>
            {t('settings.security.password.saveChanges')}
          </ActionButton>
        </div>
      </div>
      <Alert
        message={apiError || successMessage}
        type={apiError ? 'error' : 'success'}
        onClose={() => {
          setApiError('');
          setSuccessMessage('');
        }}
      />
    </div>
  );
};

export default ChangePassword;
